import { ReactNode, useState, useRef } from "react";
import { DateOfBirthField, GenderRadioField, StateField, ZipCodeField } from "components/form-fields";
import ModalTemplate, { type ModalTemplateHandle } from "../modal-template/ModalTemplate";
import { useForm, type DefaultValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import CoverageSelectField from "components/form-fields/coverage-select-field/CoverageSelectField";
import { zipCodeSchema } from "components/form-fields/zip-code-field/zipCodeFieldSchema";
import { genderSchema } from "components/form-fields/gender-field/genderFieldSchema";
import { giwlStateSchema } from "components/form-fields/state-field/stateFieldSchema";
import { DISQUALIFIED_STATES } from "constants/disqualifiedStates";
import type { UsStates } from "state/RootSchema";
import { useQuote } from "hooks/useQuote";
import { useContactInformation } from "hooks/useContactInformation";
import { useGIWLQuote } from "hooks/useGIWLQuote";
import { giwlDateOfBirthSchema } from "components/form-fields/date-of-birth-field/dateOfBirthFieldSchema";
import { QuoteData } from "store/application/applicationSlice";

// Composed from the shared GIWL field schemas so the modal and the URL parser validate the same rules.
const GIWLQuoteModalSchema = z.object({
    quote: z.object({
        dateOfBirth: giwlDateOfBirthSchema,
        state: giwlStateSchema,
        gender: genderSchema.optional(),
        zipCode: zipCodeSchema,
        selectedCoverageAmount: z.coerce.number().optional(),
    }),
});

type GIWLQuoteFormValues = z.infer<typeof GIWLQuoteModalSchema>;

// state/gender have no valid "empty" value, so they start undefined until populated from Redux on open.
const emptyQuoteValues: DefaultValues<GIWLQuoteFormValues> = {
    quote: {
        dateOfBirth: { month: "", day: "", year: "" },
        state: undefined,
        gender: undefined,
        zipCode: "",
        selectedCoverageAmount: undefined,
    },
};

const defaultTrigger = (
    <button className="btn btn-outline bg-base-200 hover:bg-white w-[124px] h-[40px] min-h-[40px] max-h-[40px] py-0 text-secondary-content hover:text-base-200 font-bold text-sm leading-none flex items-center justify-center transition-colors">
        Edit Quote
    </button>
);

type GIWLQuoteModalProps = {
    customTrigger?: ReactNode;
};

export const GIWLQuoteModal = ({ customTrigger = defaultTrigger }: GIWLQuoteModalProps) => {
    const { quoteData, saveQuote } = useQuote();
    const { contactData } = useContactInformation();
    const [formKey, setFormKey] = useState(0);
    const modalRef = useRef<ModalTemplateHandle>(null);

    const {
        register,
        formState: { errors },
        reset,
        trigger,
        watch,
        getValues,
    } = useForm<GIWLQuoteFormValues>({
        resolver: zodResolver(GIWLQuoteModalSchema),
        defaultValues: emptyQuoteValues,
        mode: "onBlur",
    });

    const quote = watch("quote");
    const fromRedux = {
        state: quoteData?.state ?? "",
        zipCode: quoteData?.zipCode ?? contactData?.contactInformation?.zipCode ?? "",
        dobMonth: quoteData?.dobMonth ?? "",
        dobDay: quoteData?.dobDay ?? "",
        dobYear: quoteData?.dobYear ?? "",
        gender: quoteData?.gender,
        selectedCoverageAmount: quoteData?.selectedCoverageAmount,
    };

    const displayState = quote?.state ?? fromRedux.state;
    const displayZipCode = quote?.zipCode ?? fromRedux.zipCode;
    const displayDobMonth = quote?.dateOfBirth?.month ?? fromRedux.dobMonth;
    const displayDobDay = quote?.dateOfBirth?.day ?? fromRedux.dobDay;
    const displayDobYear = quote?.dateOfBirth?.year ?? fromRedux.dobYear;
    const coverageAmount = quote?.selectedCoverageAmount ?? fromRedux.selectedCoverageAmount;
    const displayCoverage = coverageAmount == null ? "" : String(coverageAmount);
    const displayGender = (quote?.gender as "male" | "female" | undefined) ?? fromRedux.gender;
    const hasValidationErrors = Object.keys(errors.quote ?? {}).length > 0;

    const {
        coverageOptions,
        isLoading: isLoadingCoverage,
        error: quoteServiceError,
        noResults,
    } = useGIWLQuote({
        zipCode: displayZipCode,
        gender: displayGender,
        dobMonth: displayDobMonth,
        dobDay: displayDobDay,
        dobYear: displayDobYear,
        isMember: quoteData?.isMember,
        enabled: !hasValidationErrors && !!displayState,
    });

    // Surface either a thrown service error (server error / timeout, or a 404 "Unable to find quotes.")
    // or a successful-but-empty result as a single "unable to find quotes" message. Suppressed while a
    // request is in flight so the loading spinner — not a stale message — is what the user sees.
    const quoteMessage = isLoadingCoverage ? null : quoteServiceError ?? (noResults ? "Unable to find quotes." : null);

    // A failed quote request leaves no valid coverage to choose from, so block both the coverage
    // selector and submission until the inputs change and a request succeeds.
    const isQuoteUnavailable = hasValidationErrors || isLoadingCoverage || !!quoteServiceError;

    const handleUpdateQuote = async () => {
        const isValid = await trigger();
        if (!isValid) return;
        if (!quoteData) return;

        const values = getValues();
        const selectedCoverageAmount = Number(values.quote.selectedCoverageAmount ?? 0);
        const selectedCoveragePremium =
            coverageOptions.find((opt) => opt.coverageAmount === selectedCoverageAmount)?.monthlyPremium ?? 0;

        const updatedQuoteData: QuoteData = {
            ...quoteData,
            state: values.quote.state,
            dobMonth: values.quote.dateOfBirth.month,
            dobDay: values.quote.dateOfBirth.day,
            dobYear: values.quote.dateOfBirth.year,
            gender: (values.quote.gender as "male" | "female") ?? quoteData.gender,
            zipCode: values.quote.zipCode,
            selectedCoverageAmount,
            selectedCoveragePremium,
        };

        saveQuote(updatedQuoteData);
        modalRef.current?.close();
    };

    const handleModalOpen = () => {
        if (!quoteData) return;
        reset({
            quote: {
                dateOfBirth: {
                    month: fromRedux.dobMonth,
                    day: fromRedux.dobDay,
                    year: fromRedux.dobYear,
                },
                state: fromRedux.state as UsStates,
                gender: fromRedux.gender,
                zipCode: fromRedux.zipCode,
                selectedCoverageAmount: fromRedux.selectedCoverageAmount ?? undefined,
            },
        });
        setTimeout(() => {
            setFormKey((k) => k + 1);
            void trigger("quote.dateOfBirth");
        }, 0);
    };

    return (
        <ModalTemplate
            ref={modalRef}
            trigger={customTrigger}
            modalId="giwl-quote-modal"
            className="p-0 tablet:max-w-[448px] max-w-[370px] text-base-content"
            onOpen={handleModalOpen}
        >
            <div key={formKey} className="flex flex-col max-h-[calc(100vh-8rem)] tablet:max-h-none text-base-content">
                <div className="flex flex-col flex-shrink-0 px-6 pt-4 bg-base-300 h-[88px]">
                    <div className="flex flex-col gap-2">
                        <p className="font-medium text-base leading-5 text-base-content">Guaranteed Issue</p>
                        <p className="font-bold text-2xl leading-5 text-secondary-content">Whole Life</p>
                    </div>
                    <div className="border-b-2 border-base-content/10 mt-4 -mx-6 tablet:hidden"></div>
                </div>
                <form
                    className="flex flex-col gap-4 px-6 pb-6 mt-4 text-base-content"
                    data-testid="giwl-quote-form"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <div className="flex flex-col text-center font-bold text-xl text-base-content max-h-[calc(100vh-8rem)] tablet:max-h-none">
                        Edit your quote
                    </div>
                    <DateOfBirthField
                        monthRegister={register("quote.dateOfBirth.month")}
                        dayRegister={register("quote.dateOfBirth.day")}
                        yearRegister={register("quote.dateOfBirth.year")}
                        monthValue={displayDobMonth}
                        dayValue={displayDobDay}
                        yearValue={displayDobYear}
                        monthError={errors.quote?.dateOfBirth?.month?.message}
                        dayError={errors.quote?.dateOfBirth?.day?.message}
                        yearError={errors.quote?.dateOfBirth?.year?.message}
                        id="giwl-quote-dateOfBirth"
                        trigger={trigger}
                    />
                    <div className="flex gap-3">
                        <div className="flex-[2] min-w-0">
                            <StateField
                                register={register("quote.state")}
                                error={errors.quote?.state?.message}
                                value={displayState}
                                id="giwl-quote-state"
                                shouldNotShow={DISQUALIFIED_STATES.giwl}
                                fullWidth
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <ZipCodeField
                                register={register("quote.zipCode")}
                                error={errors.quote?.zipCode?.message}
                                value={displayZipCode}
                                id="giwl-quote-zipCode"
                                disabled
                            />
                        </div>
                    </div>
                    <GenderRadioField
                        register={register("quote.gender")}
                        error={errors.quote?.gender?.message}
                        value={displayGender}
                        maleId="giwl-quote-gender-male"
                        femaleId="giwl-quote-gender-female"
                        onChange={() => trigger("quote.gender")}
                    />
                    <CoverageSelectField
                        register={register("quote.selectedCoverageAmount")}
                        value={displayCoverage}
                        id="giwl-quote-coverage"
                        coverageOptions={coverageOptions}
                        disabled={isQuoteUnavailable}
                    />
                    {quoteMessage && (
                        <div data-testid="giwl-quote-error" className="bg-warning border border-warning rounded-lg p-4">
                            <p className="text-warning-content font-medium text-sm leading-5">{quoteMessage}</p>
                        </div>
                    )}
                    <div className="flex justify-center">
                        <button
                            type="button"
                            onClick={handleUpdateQuote}
                            disabled={isQuoteUnavailable}
                            className="btn btn-outline bg-secondary-content hover:bg-white w-[148px] h-[36px] min-h-[40px] max-h-[40px] py-0 text-white hover:text-secondary-content font-bold text-sm leading-none flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Update Quote
                            {isLoadingCoverage && (
                                <span data-testid="loading-spinner" className="loading loading-spinner loading-sm" />
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </ModalTemplate>
    );
};
